




























































































































import _, {Dictionary} from 'lodash'
import Vue from 'vue'
import Component from 'vue-class-component'
import {Getter, State, Mutation, Action} from 'vuex-class'
import {Watch} from 'vue-property-decorator'
import {Route} from 'vue-router'
import MessageDetailComponent from '@/modules/i18n/components/MessageDetail.vue'
import {MutationTypes, MessageListModes} from '@/modules/i18n/store/mutations'
import MessageDeletionConfirmComponent from '@/modules/i18n/components/MessageDeletionConfirm.vue'
import LocaleSelectComponent from '@/modules/i18n/components/LocaleSelect.vue'
import MessageItem from '@/modules/i18n/components/MessageItem.vue';
import {notify, Colors} from '@/modules/core/notification'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import Log from '@/modules/log'
import {Config} from '@/modules/core/config'
import MessagePagination from '@/modules/i18n/model/MessagePagination'
import Message from '@/modules/i18n/model/Message'
import Locale from '@/modules/i18n/model/Locale'
import Loader from '@/components/Loader.vue'

/**
 * Lista de mensajes
 *
 * Muestra al usuario una cuadricula con los mensajes de traducción registrados
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {
        Loader,
        LocaleSelectComponent,
        'message-detail': MessageDetailComponent,
        'message-deletion-confirm': MessageDeletionConfirmComponent,
        'message-item': MessageItem,
    },
})
export default class MessageListComponent extends Vue {

    public $refs: {
        localeSelect: LocaleSelectComponent,
    };

    @State(state => state.config)
    public config: Config;

    /**
     * Localismo/idioma actualmente seleccionado por el usuario
     */
    @State(state => state.i18n.messageList.selectedLocale)
    public selectedLocale: string;

    /**
     * Modo
     *
     * Indica si la lista esta actualmente editando o creando algún mensaje
     * del localismo seleccionado
     */
    @State(state => state.i18n.messageList.mode)
    public mode: string;

    /**
     * Página meostrada actualmente al usuario
     */
    @State(state => state.i18n.messageList.currentPage)
    public currentPage: number;

    /**
     * Cantidad de registros por página mostrados en la paginación de mensajes
     */
    @State(state => state.i18n.messageList.recordsPerPage)
    public recordsPerPage: number;

    /**
     * Filtros de paginación (busqueda por clave, etc...)
     */
    @State(state => state.i18n.messageList.paginationFilters)
    public paginationFilters: KeyValuePair;

    /**
     * Paginación obtenida del servidor mostrada actualmente al usuario
     */
    @State(state => state.i18n.messageList.pagination)
    public pagination: MessagePagination;

    /**
     * Lista de mensajes/traducciones registrados en el localismo selecionado
     * por el usuario
     */
    @Getter('i18n/localeMessages')
    public localeMessages: (locale: string) => Message[];

    /**
     * Objeto del localismo seleccionado actualmente
     */
    @Getter('i18n/currentLocale')
    public currentLocale: Locale | null;

    /**
     * Getter de Vuex que permite obtener una entidad Locale en base al
     * identificador argumentado
     */
    @Getter('i18n/locale')
    public locale: (identifier: string) => Locale | null;

    /**
     * Getter de Vuex que permite comprobar si existe algún localismo en la
     * aplicación asociado con el identificador argumetado
     */
    @Getter('i18n/localeExists')
    public localeExists: (identifiers: string) => boolean;

    /**
     * Mutación del estado que permite modificar el localismo seleccionado por
     * el usuario
     */
    @Mutation(`i18n/${MutationTypes.SetMessageListSelectedLocale}`)
    public setSelectedLocale: (payload: { locale: string | null }) => void;

    @Mutation(`i18n/${MutationTypes.SetLocaleMessageMode}`)
    public setMode: (payload: { mode: MessageListModes }) => void;

    @Mutation(`i18n/${MutationTypes.SetMessageListSelectedMessage}`)
    public selectMessage: (payload: { message: Message | null }) => void;

    /**
     * Mutación que cambia la página actual mostrada
     */
    @Mutation(`i18n/${MutationTypes.SetMessageListCurrentPage}`)
    public setCurrentPage: (page: number) => void;

    @Mutation(`i18n/${MutationTypes.SetMessageListCurrentFilters}`)
    public setCurrentPaginationFilters: (filters: KeyValuePair) => void;

    /**
     * Carga una paginación de mensajes
     */
    @Action('i18n/loadMessagePagination')
    public loadPagination: () => void;

    /**
     * Carga la lista de idiomas
     */
    @Action('i18n/loadLocales')
    public loadLocale: () => void;

    public loading = true;
    public reloading = false;
    public searchMenu = false

    get isSaving (): boolean {
        return this.mode === MessageListModes.Editing || this.mode === MessageListModes.Creating;
    }

    get isDeleting (): boolean {
        return this.mode === MessageListModes.Deleting;
    }

    /**
     * Propiedad conmutada que permite acceder a los mensajes del localismo
     * actualmente seleccionado
     */
    get messages () {
        return this.localeMessages(this.selectedLocale);
    }

    /**
     * Indica se se ha activado el modo "único idioma" en la configuración
     * del panel.
     */
    get singleLanguage (): boolean {
        return this.config.get('i18n.singleLanguage', false);
    }

    /**
     * Recarga la lista de mensajes
     */
    @Watch('$route')
    public async onRoute (to: Route) {
        await this.onLocale(to.params['locale'] || '__');
        await this.onPagination(to.query);
    }

    /**
     * Método retardado que modificará la paginación cuando el usuario introduzca
     * un termino de búsqueda en la caja de texto.
     */
    public onPaginationSearch = _.debounce(function (this: MessageListComponent, search: string) {
        const filters: { searchByKey?: string } = {};
        if (search) {
            filters.searchByKey = search;
        }

        this.changePaginationRoute(this.currentPage, this.recordsPerPage, filters);
    }, 800);

    /**
     * Método invocado cada vez que es modificada la paginación
     */
    public async onPagination (query: Dictionary<string> | any) {
        await this.$vuetify.goTo(0, {
            easing: 'easeInOutCubic',
        });

        this.reloading = true;
        const currentFilters = _.clone(this.paginationFilters);
        const {page, recordsPerPage, searchByKey} = query;
        if (page) {
            this.setCurrentPage(_.toInteger(page));
        }

        if (searchByKey) {
            currentFilters.searchByKey = searchByKey;
        } else {
            delete currentFilters.searchByKey;
        }

        this.setCurrentPaginationFilters(currentFilters);

        await this.loadPagination();
        this.reloading = false;
    }

    /**
     * Cambiar parámetros de paginación de la URL
     *
     * Invocado cada vez que el usuario cambia de página o realiza una busqueda
     * por termino. Modifica la url actual para añadir los parámetros de
     * la paginación
     */
    public changePaginationRoute (page: number, recordsPerPage: number, filters: KeyValuePair = {}) {
        // Si la nueva paginación es igual a la cargada actualmente se detiene el proceso
        if (this.paginationIsEqual(page, recordsPerPage, filters)) {
            return;
        }

        if (page > this.pagination.totalPages) {
            return;
        }

        this.$router.push({
            name: 'i18n.translations',
            params: {locale: this.currentLocale!.identifier},
            query: {
                page: page.toString(),
                recordsPerPage: recordsPerPage.toString(),
                ...filters,
            },
        });
    }

    /**
     * Comprueba si los parámetros de paginación argumentados son idénticos a
     * los parámetros de paginación actuales
     */
    public paginationIsEqual (page: number, recordsPerPage: number, filters: KeyValuePair = {}): boolean {
        const newParams = {page, recordsPerPage, filters};

        // Parámetro de paginación actuales
        const currentParams = {
            page: this.currentPage,
            recordsPerPage: this.recordsPerPage,
            filters: this.paginationFilters,
        };

        return JSON.stringify(newParams) === JSON.stringify(currentParams);
    }

    /**
     * Handler del input de selección
     *
     * Carga en el estado el último localismo seleccionado
     */
    public onSelect (selected: Locale) {
        this.$router.push({
            name: 'i18n.translations',
            params: {locale: selected.identifier},
        });
    }


    /**
     * Muestra la pantalla para editar el mensaje seleccionado
     */
    public createMessage (): void {
        if (!this.currentLocale) {
            return;
        }

        this.setMode({mode: MessageListModes.Creating});
        this.selectMessage({message: new Message('', '', this.selectedLocale)});

        Log.info('El usuario ha abierto la pantalla para crear una nueva traducción');
    }


    public async onLocale (locale: string) {
        const selectedLocale = this.currentLocale;

        // Si es el mismo idioma se detiene el proceso
        if (selectedLocale && selectedLocale.identifier === locale) {
            return;
        }

        // Si el localismo es "__" y ya se ha seleccionado un localismo con aterioridad,
        // se cargara el actual
        if (locale === '__' && selectedLocale) {
            await this.$router.push({name: 'i18n.translations', params: {locale: selectedLocale.identifier}});

            return;
        }

        // Si no existe un localismo seleccionado anteriormente, se intentará
        // carga el idioma Español
        if (locale === '__') {
            const spa = this.locale('es');
            if (spa) {
                await this.$router.push({name: 'i18n.translations', params: {locale: spa.identifier}});
            }

            return;
        }

        // Si el localismo no esta registrado en la aplicación termina el
        // proceso y se muestra al usuario la pantalla de idiomas vacia
        if (!this.localeExists(locale)) {
            notify(`El localismo "${locale}" no se encuentra registrado en la aplicación`, Colors.Warning);
            await this.$router.push({name: 'i18n.translations', params: {locale: '__'}});

            return;
        }

        this.setSelectedLocale({locale});
    }

    /**
     * Hook de creación del componente
     *
     * Si la ruta es "__" y existe algún localismo seleccionado en el estado
     * se cargará este.
     */
    public async mounted () {
        window.addEventListener('scroll', () => this.searchMenu = false)
        // Carga la lista de idiomas
        await this.loadLocale();

        // Se encuentra activo el modo "único idioma"
        if (this.singleLanguage) {
            await this.onLocale('es');
            await this.onPagination({});
            this.loading = false;

            return;
        }

        const locale = this.$route.params.locale;
        const query = this.$route.query;

        await this.onLocale(locale);

        // Carga la primera paginación de mensajes
        await this.onPagination(query);
        this.loading = false;
    }
}
